import { Button, EmptyState, Skeleton, cn } from '@paguz/ui';
import {
  CircleAlertIcon,
  CircleCheckIcon,
  InfoIcon,
  TriangleAlertIcon,
  WifiOffIcon,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { customerMessage } from '../api/errors.ts';

type AlertTone = 'info' | 'success' | 'warning' | 'danger';

const TONES: Record<AlertTone, { className: string; icon: typeof InfoIcon }> = {
  info: { className: 'bg-highlight-soft text-highlight-soft-foreground', icon: InfoIcon },
  success: { className: 'bg-success-soft text-success-soft-foreground', icon: CircleCheckIcon },
  warning: { className: 'bg-warning-soft text-warning-soft-foreground', icon: TriangleAlertIcon },
  danger: { className: 'bg-danger-soft text-danger-soft-foreground', icon: CircleAlertIcon },
};

/**
 * Aviso en línea (local: `@paguz/ui` aún no tiene `Alert`, ver Solicitudes).
 * `danger` usa `role="alert"` para que el lector de pantalla lo anuncie.
 */
export function InlineAlert({
  tone = 'info',
  title,
  children,
  action,
  className,
}: {
  tone?: AlertTone;
  title?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  const { className: toneClass, icon: Icon } = TONES[tone];
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cn('flex gap-3 rounded-lg p-3 text-sm', toneClass, className)}
    >
      <Icon className="mt-0.5 size-5 shrink-0" aria-hidden />
      <div className="flex flex-1 flex-col gap-1">
        {title !== undefined && <p className="font-semibold">{title}</p>}
        {children !== undefined && <div>{children}</div>}
        {action}
      </div>
    </div>
  );
}

/** Estado de error de una consulta con botón de reintento. */
export function QueryErrorState({
  error,
  onRetry,
  className,
}: {
  error: unknown;
  onRetry?: () => void;
  className?: string;
}) {
  return (
    <EmptyState
      className={className}
      icon={<WifiOffIcon aria-hidden />}
      title="No pudimos cargar esta información"
      description={customerMessage(error)}
      action={
        onRetry ? (
          <Button variant="outline" onClick={onRetry}>
            Intentar de nuevo
          </Button>
        ) : undefined
      }
    />
  );
}

export function ListSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="flex flex-col gap-3" aria-busy="true" aria-label="Cargando">
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} className="h-16 w-full rounded-lg" />
      ))}
    </div>
  );
}
